// рішення з доки base-ui https://v6.mui.com/base-ui/react-input/

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Fragment, useRef } from 'react';

type OtpInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  separator?: boolean;
};

export function OtpInput({ length = 6, value, onChange, separator }: OtpInputProps) {
  const inputRefs = useRef<HTMLInputElement[]>(
    Array.from({ length }, () => null as unknown as HTMLInputElement),
  );

  const focusInput = (index: number) => inputRefs.current[index]?.focus();
  const selectInput = (index: number) => inputRefs.current[index]?.select();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    switch (e.key) {
      case ' ':
        e.preventDefault();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (index > 0) {
          focusInput(index - 1);
          selectInput(index - 1);
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (index < length - 1) {
          focusInput(index + 1);
          selectInput(index + 1);
        }
        break;
      case 'Backspace':
        e.preventDefault();
        onChange(value.slice(0, index) + value.slice(index + 1));
        if (index > 0) {
          focusInput(index - 1);
          selectInput(index - 1);
        }
        break;
      case 'Delete':
        e.preventDefault();
        onChange(value.slice(0, index) + value.slice(index + 1));
        break;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const char = e.target.value.slice(-1);
    if (!char) return;
    const next = value.split('');
    next[index] = char;
    onChange(next.join(''));
    if (index < length - 1) {
      focusInput(index + 1);
      selectInput(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const text = e.clipboardData
      .getData('text/plain')
      .trim()
      .slice(0, length - index);
    if (!text) return;
    const next = value.split('');
    for (let i = 0; i < text.length; i++) {
      next[index + i] = text[i] ?? '';
    }
    onChange(next.join(''));
    const jumpTo = Math.min(index + text.length, length - 1);
    focusInput(jumpTo);
    selectInput(jumpTo);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {Array.from({ length }).map((_, index) => (
        <Fragment key={index}>
          {index === Math.floor(length / 2) && separator && <Separator>-</Separator>}
          <InputElement
            ref={(el) => {
              inputRefs.current[index] = el!;
            }}
            inputMode="numeric"
            value={value[index] ?? ''}
            placeholder="0"
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onClick={() => selectInput(index)}
            onPaste={(e) => handlePaste(e, index)}
          />
        </Fragment>
      ))}
    </Box>
  );
}

const Separator = styled(Box)(({ theme }) => ({
  fontSize: 40,
  lineHeight: 1,
  userSelect: 'none',
  color: theme.palette.gray[300],

  ...theme.applyStyles('dark', {
    color: theme.palette.black[600],
  }),
}));

const InputElement = styled('input')(({ theme }) => ({
  width: 64,
  height: 64,
  fontSize: 28,
  fontWeight: 600,
  fontFamily: theme.typography.fontFamily,
  textAlign: 'center',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.gray[200]}`,
  backgroundColor: 'transparent',
  color: theme.palette.black[950],
  cursor: 'text',
  outline: 'none',
  transition: 'border-color 0.3s, color 0.3s, box-shadow 0.5s',

  '&::placeholder': {
    color: theme.palette.gray[300],
    opacity: 1,
  },

  '&:not(:placeholder-shown)': {
    border: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
  },

  '&:focus': {
    border: `2px solid ${theme.palette.primary.main}`,
    outline: 'none',
    boxShadow: `0 0 0 2px ${theme.palette.background.default}, 0 0 0 4px ${theme.palette.primary.main}`,
  },

  '&:focus-visible': {
    outline: 'none',
  },

  ...theme.applyStyles('dark', {
    border: `1px solid ${theme.palette.black[600]}`,
    color: theme.palette.black[25],

    '&::placeholder': {
      color: theme.palette.black[300],
      opacity: 1,
    },
  }),
}));
